import { useState } from "react"
import { useData } from "@/contexts/data-context"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart,
} from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"

interface DataPoint {
  time: string
  value: number
  timestamp: number
}

interface SensorChartProps {
  sensorId: string
}

export default function SensorChart({ sensorId }: SensorChartProps) {
  const { sensors } = useData()
  const sensor = sensors.find((s) => s.id === sensorId)
  const [timeRange, setTimeRange] = useState<"5min" | "15min" | "1hour">("15min")

  if (!sensor) return null

  // Format data for chart
  const chartData = sensor.history.map((point) => ({
    time: point.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    value: point.value,
    timestamp: point.timestamp.getTime(),
  }))

  // Calculate min and max for chart
  const dataMin = Math.min(...chartData.map((d) => d.value))
  const dataMax = Math.max(...chartData.map((d) => d.value))

  // Add some padding to the min and max
  const range = dataMax - dataMin
  const paddedMin = Math.max(sensor.min, dataMin - range * 0.1)
  const paddedMax = Math.min(sensor.max, dataMax + range * 0.1)

  // Calculate warning and critical thresholds
  const warningThreshold = 0.15 // 15% from min/max
  const criticalThreshold = 0.05 // 5% from min/max

  const minWarning = sensor.min + (sensor.max - sensor.min) * warningThreshold
  const maxWarning = sensor.max - (sensor.max - sensor.min) * warningThreshold
  const minCritical = sensor.min + (sensor.max - sensor.min) * criticalThreshold
  const maxCritical = sensor.max - (sensor.max - sensor.min) * criticalThreshold

  return (
    <Card className="mt-4">
      <CardContent className="pt-4">
        <Tabs defaultValue="line" className="w-full">
          <div className="flex justify-between items-center mb-4">
            <TabsList>
              <TabsTrigger value="line">Line</TabsTrigger>
              <TabsTrigger value="area">Area</TabsTrigger>
            </TabsList>

            <TabsList>
              <TabsTrigger value="5min" onClick={() => setTimeRange("5min")}>
                5m
              </TabsTrigger>
              <TabsTrigger value="15min" onClick={() => setTimeRange("15min")}>
                15m
              </TabsTrigger>
              <TabsTrigger value="1hour" onClick={() => setTimeRange("1hour")}>
                1h
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="line">
            <ChartContainer
              config={{
                value: {
                  label: `${sensor.name} (${sensor.unit})`,
                  color: "hsl(var(--chart-1))",
                },
              }}
              className="h-[200px]"
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} tickMargin={5} stroke="hsl(var(--muted-foreground))" />
                  <YAxis
                    domain={[paddedMin, paddedMax]}
                    tick={{ fontSize: 10 }}
                    tickMargin={5}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />

                  {/* Warning and critical thresholds */}
                  <ReferenceLine y={minWarning} stroke="hsl(var(--warning))" strokeDasharray="3 3" />
                  <ReferenceLine y={maxWarning} stroke="hsl(var(--warning))" strokeDasharray="3 3" />
                  <ReferenceLine y={minCritical} stroke="hsl(var(--destructive))" strokeDasharray="3 3" />
                  <ReferenceLine y={maxCritical} stroke="hsl(var(--destructive))" strokeDasharray="3 3" />

                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="var(--color-value)"
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                    animationDuration={300}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </TabsContent>

          <TabsContent value="area">
            <ChartContainer
              config={{
                value: {
                  label: `${sensor.name} (${sensor.unit})`,
                  color: "hsl(var(--chart-1))",
                },
              }}
              className="h-[200px]"
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} tickMargin={5} stroke="hsl(var(--muted-foreground))" />
                  <YAxis
                    domain={[paddedMin, paddedMax]}
                    tick={{ fontSize: 10 }}
                    tickMargin={5}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />

                  {/* Warning and critical thresholds */}
                  <ReferenceLine y={minWarning} stroke="hsl(var(--warning))" strokeDasharray="3 3" />
                  <ReferenceLine y={maxWarning} stroke="hsl(var(--warning))" strokeDasharray="3 3" />
                  <ReferenceLine y={minCritical} stroke="hsl(var(--destructive))" strokeDasharray="3 3" />
                  <ReferenceLine y={maxCritical} stroke="hsl(var(--destructive))" strokeDasharray="3 3" />

                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="var(--color-value)"
                    fill="var(--color-value)"
                    fillOpacity={0.2}
                    strokeWidth={2}
                    activeDot={{ r: 4 }}
                    animationDuration={300}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>
          </TabsContent>
        </Tabs>

        <div className="mt-2 text-xs text-muted-foreground">
          <div className="flex items-center justify-between">
            <div>
              Min: {sensor.min} {sensor.unit}
            </div>
            <div>
              Max: {sensor.max} {sensor.unit}
            </div>
          </div>
          <div className="flex items-center justify-between mt-1">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-warning rounded-full"></div>
              <span>Warning Threshold</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-destructive rounded-full"></div>
              <span>Critical Threshold</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
